/**
 * One report, whole: its definition and versions, every run, its schedule, why
 * nothing is being sent, and the delivery record for just this report.
 *
 * Reached from the library ("Open full page"), by double-click, and by link:
 * `/reports?report=<key>`. Everything on it is the same data the library reads,
 * so the two cannot disagree.
 */
import { useMemo, useState } from 'react'
import { Copy, Play, Share2, SlidersHorizontal } from 'lucide-react'
import clsx from 'clsx'
import type { ReportDefinition } from '@/services/reportDefinitions'
import { reportTypeSpec } from '../reportTypes'
import { deploymentNotice, deploymentSwitches } from './attention'
import { DeleteReport } from './DeleteReport'
import { DeliveryTable } from './DeliveryTable'
import type { DeliveryFilter } from './deliveries'
import { formatAgo, formatDay, plural } from './format'
import type { LibraryReport } from './library'
import type { ReportHandlers } from './ReportPanel'
import { RunsList } from './RunsList'
import { ScheduleCard } from './ScheduleCard'
import { Btn, Eyebrow, KeyValue, Panel } from './ui'
import type { ReportActions } from './useReportActions'
import type { ReportingData, ReportPermissions } from './useReportingData'

const NO_RUN = 'Your role cannot run reports.'
const NO_EDIT = 'Your role can view and run reports but not change them.'

/** Filters worth printing: plain values, with the plumbing left out. */
function filterLines(filters: Record<string, unknown>): string[] {
  const lines: string[] = []
  for (const [key, value] of Object.entries(filters)) {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) continue
    if (/^(data_partition|workspace_id|viewGrid|view_grid)$/i.test(key)) continue
    let text: string
    if (Array.isArray(value)) text = value.map(String).join(', ')
    else if (typeof value === 'object') continue
    else text = String(value)
    const label = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ')
    lines.push(`${label.charAt(0).toUpperCase()}${label.slice(1)}: ${text}`)
  }
  return lines
}

function Versions({
  definition,
  actions,
  perms,
}: {
  definition: ReportDefinition
  actions: ReportActions
  perms: ReportPermissions
}) {
  const [restoring, setRestoring] = useState<number | null>(null)
  const versions = definition.versions ?? []
  return (
    <Panel as="section" aria-label="Versions" className="flex flex-col gap-2 p-4">
      <Eyebrow>VERSIONS</Eyebrow>
      {versions.length === 0 ? (
        <p className="text-[12.5px] text-[#8F8F8F]">No version history is recorded for this report.</p>
      ) : (
        <ul className="flex flex-col">
          {versions.slice(0, 8).map((version) => {
            const current = version.version === definition.version
            return (
              <li key={version.id} className="flex gap-2.5 border-t border-[#151515] py-2 first:border-t-0">
                <span className="w-[30px] shrink-0 font-semibold text-[#FFD86B]">v{version.version}</span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-[13px] text-[#ECEAE4]">{version.note || `Version ${version.version}`}</span>
                  <span className="block text-[11.5px] text-[#8F8F8F]">
                    {version.authorEmail} · {formatDay(version.createdAt)}
                  </span>
                </span>
                <Btn
                  size="sm"
                  variant="quiet"
                  disabled={current || !perms.canEdit || actions.restore.isPending}
                  title={current ? 'This is the current version.' : perms.canEdit ? undefined : NO_EDIT}
                  aria-label={current ? `Version ${version.version} is current` : `Restore version ${version.version}`}
                  onClick={() => setRestoring(restoring === version.version ? null : version.version)}
                  className="self-start"
                >
                  {current ? 'Current' : 'Restore'}
                </Btn>
              </li>
            )
          })}
        </ul>
      )}
      {restoring !== null ? (
        <div role="alertdialog" aria-label={`Restore version ${restoring}?`} className="rounded-lg border border-[#4A3F1E] bg-[#0E0D09] p-3 text-[12.5px] text-[#E8D9A8]">
          <p>
            Restoring v{restoring} saves it as a new version. v{definition.version} stays in the history.
          </p>
          <div className="mt-2 flex gap-2">
            <Btn
              size="sm"
              variant="primary"
              busy={actions.restore.isPending}
              onClick={() =>
                actions.restore.mutate(
                  { id: definition.id, version: restoring },
                  { onSettled: () => setRestoring(null) },
                )
              }
            >
              Restore v{restoring}
            </Btn>
            <Btn size="sm" variant="quiet" onClick={() => setRestoring(null)}>
              Cancel
            </Btn>
          </div>
        </div>
      ) : null}
    </Panel>
  )
}

export function ReportDetailPage({
  report,
  data,
  actions,
  perms,
  handlers,
  deliveryFilter,
  onDeliveryFilter,
  onDeleted,
}: {
  report: LibraryReport
  data: ReportingData
  actions: ReportActions
  perms: ReportPermissions
  handlers: ReportHandlers
  deliveryFilter: DeliveryFilter
  onDeliveryFilter: (filter: DeliveryFilter) => void
  onDeleted: () => void
}) {
  const definition = report.definition
  const spec = definition ? reportTypeSpec(definition.reportType) : null
  const notice = deploymentNotice(data.status)
  const switches = deploymentSwitches(data.status)
  const running = actions.run.isPending && actions.run.variables?.report.key === report.key
  const scheduleIds = useMemo(() => new Set(report.schedules.map((schedule) => schedule.id)), [report.schedules])
  const rows = useMemo(() => data.deliveries.filter((row) => scheduleIds.has(row.scheduleId)), [data.deliveries, scheduleIds])
  const lines = definition ? filterLines(definition.filters) : []
  const meta = [
    definition ? `Version ${definition.version}` : null,
    report.owner === 'You' ? 'owned by you' : `owned by ${report.owner}`,
    report.sharing.toLowerCase(),
    report.formats,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="report-detail">
      <header className="flex flex-wrap items-center gap-5 border-b border-[#1E1E1E] bg-[#080808] px-6 pb-4 pt-5">
        <div className="flex min-w-0 flex-[1_1_480px] flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <span className="rounded border border-[#3A3220] px-1.5 py-0.5 text-[10.5px] tracking-[.14em] text-[#FFD86B]">
              {report.bucket.code}
            </span>
            <span className="text-[13px] text-[#8F8F8F]">
              {spec ? spec.name : report.bucket.name} · {report.scope}
            </span>
          </div>
          <h1 className="font-display text-[36px] font-semibold leading-none text-[#F5F3EE]">{report.name}</h1>
          <p className="text-[13px] text-[#B9B9B9]">{meta}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Btn
            variant="primary"
            icon={<Play className="h-3.5 w-3.5" aria-hidden />}
            busy={running}
            disabled={!perms.canRun}
            title={perms.canRun ? undefined : NO_RUN}
            onClick={() => actions.run.mutate({ report })}
            aria-label={`Run ${report.name} now`}
          >
            Run now
          </Btn>
          <Btn
            icon={<SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />}
            onClick={() => handlers.open(report)}
            aria-label={`${definition ? 'Edit' : 'Configure'} ${report.name}`}
          >
            {definition ? 'Edit definition' : 'Configure'}
          </Btn>
          {definition ? (
            <>
              <Btn
                icon={<Copy className="h-3.5 w-3.5" aria-hidden />}
                disabled={!perms.canEdit || actions.duplicate.isPending}
                title={perms.canEdit ? undefined : NO_EDIT}
                onClick={() => actions.duplicate.mutate(definition.id)}
                aria-label={`Duplicate ${report.name}`}
              >
                Duplicate
              </Btn>
              <Btn
                icon={<Share2 className="h-3.5 w-3.5" aria-hidden />}
                disabled={!perms.canEdit}
                title={perms.canEdit ? undefined : NO_EDIT}
                onClick={() => handlers.share(report)}
                aria-label={`Share ${report.name}`}
              >
                Share
              </Btn>
              <DeleteReport report={report} actions={actions} perms={perms} onDeleted={onDeleted} />
            </>
          ) : null}
        </div>
      </header>

      {notice ? (
        <Panel
          as="section"
          aria-label="What this deployment will do"
          tone={notice.tone === 'ok' ? 'plain' : 'gold'}
          className="mx-6 mt-5 flex flex-wrap items-center gap-5 p-4"
        >
          <div className="flex min-w-[260px] flex-[1_1_380px] flex-col gap-1.5">
            <Eyebrow tone="gold">WHAT THIS DEPLOYMENT WILL DO</Eyebrow>
            <div className="font-display text-[22px] font-semibold leading-tight text-[#F5F3EE]">{notice.title}</div>
            <p className="text-[13px] text-[#B9B9B9]">
              {notice.body} This page shows the state; an administrator changes it in settings.
            </p>
          </div>
          <ul className="flex flex-[1_1_460px] flex-wrap gap-2" aria-label="Deployment switches">
            {switches.map((entry) => (
              <li key={entry.label} className="min-w-[130px] flex-[1_1_130px] rounded-lg border border-[#2A2A2A] bg-[#0B0B0B] px-2.5 py-2">
                <div className="text-[11px] text-[#8F8F8F]">{entry.label}</div>
                <div className={clsx('text-[13px] font-semibold', entry.on ? 'text-[#5FD38D]' : entry.value === 'Unknown' ? 'text-[#B9B9B9]' : 'text-[#F0B08A]')}>
                  {entry.value}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <div className="flex flex-wrap items-start gap-5 px-6 pb-6 pt-5">
        <aside aria-label="Definition" className="flex min-w-[260px] max-w-[340px] flex-[1_1_280px] flex-col gap-4">
          <Panel as="section" className="flex flex-col gap-3 p-4">
            <Eyebrow>DEFINITION</Eyebrow>
            {definition && spec ? (
              <dl className="flex flex-col gap-2">
                <KeyValue label="Type">{spec.name}</KeyValue>
                <KeyValue label="Scope">{report.scope}</KeyValue>
                {definition.columns.length ? <KeyValue label="Columns">{definition.columns.length}</KeyValue> : null}
                <KeyValue label="Formats">{report.formats}</KeyValue>
                <KeyValue label="Last changed">{formatDay(definition.updatedAt)}</KeyValue>
              </dl>
            ) : (
              <p className="text-[12.5px] text-[#B9B9B9]">
                This is not a saved report. It is listed because{' '}
                {report.kind === 'schedule' ? 'a schedule exists for it' : 'files were generated from it'}. Open
                Configure to set it up as one you can change and version.
              </p>
            )}
            {lines.length ? (
              <div className="border-t border-[#1B1B1B] pt-3">
                <div className="mb-1.5 text-[12px] text-[#8F8F8F]">Filters</div>
                <ul className="flex flex-col gap-1 text-[12.5px] text-[#D8D8D8]">
                  {lines.slice(0, 6).map((line) => (
                    <li key={line} className="break-words">{line}</li>
                  ))}
                  {lines.length > 6 ? <li className="text-[#8F8F8F]">and {lines.length - 6} more</li> : null}
                </ul>
              </div>
            ) : null}
          </Panel>

          {definition ? <Versions definition={definition} actions={actions} perms={perms} /> : null}

          {definition ? (
            <Panel as="section" aria-label="Who can open it" className="flex flex-col gap-2 p-4">
              <Eyebrow>WHO CAN OPEN IT</Eyebrow>
              <div className="flex justify-between gap-3 text-[13px]">
                <span className="break-all text-[#ECEAE4]">{report.owner === 'You' ? 'You' : definition.ownerEmail}</span>
                <span className="shrink-0 text-[#8F8F8F]">Owner</span>
              </div>
              {definition.editors.map((email) => (
                <div key={`e-${email}`} className="flex justify-between gap-3 text-[13px]">
                  <span className="break-all text-[#ECEAE4]">{email}</span>
                  <span className="shrink-0 text-[#8F8F8F]">Editor</span>
                </div>
              ))}
              {definition.viewers.map((email) => (
                <div key={`v-${email}`} className="flex justify-between gap-3 text-[13px]">
                  <span className="break-all text-[#ECEAE4]">{email}</span>
                  <span className="shrink-0 text-[#8F8F8F]">Viewer</span>
                </div>
              ))}
              <div className="flex justify-between gap-3 text-[13px]">
                <span className="text-[#ECEAE4]">Everyone who can see reports</span>
                <span className="shrink-0 text-[#8F8F8F]">{definition.isShared || definition.isSystem ? 'Viewer' : 'No access'}</span>
              </div>
            </Panel>
          ) : null}
        </aside>

        <section aria-label="Runs" className="min-w-0 flex-[999_1_460px]">
          <Panel className="p-4">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <h2 className="text-[15px] font-semibold text-[#F5F3EE]">Runs</h2>
              <span className="text-[12px] text-[#8F8F8F]">
                {report.runs.length ? `${plural(report.runs.length, 'file')}, newest ${formatAgo(report.runs[0].at, data.now)}` : 'No files yet'}
              </span>
            </div>
            <RunsList report={report} actions={actions} perms={perms} now={data.now} initiallyShown={8} />
          </Panel>
        </section>

        <aside aria-label="Schedule" className="flex min-w-[300px] max-w-[400px] flex-[1_1_340px] flex-col gap-4">
          {report.health.length ? (
            report.health.map(({ schedule, health }) => (
              <ScheduleCard
                key={schedule.id}
                schedule={schedule}
                health={health}
                actions={actions}
                perms={perms}
                me={data.me}
                now={data.now}
                onEdit={handlers.editSchedule}
              />
            ))
          ) : (
            <Panel as="section" className="flex flex-col gap-3 p-4">
              <Eyebrow>SCHEDULE</Eyebrow>
              <p className="text-[13px] text-[#B9B9B9]">
                Not scheduled. Run it by hand, or schedule it. A new schedule is saved paused and starts only when
                someone with approval activates it.
              </p>
              <Btn
                size="sm"
                disabled={!perms.canEdit}
                title={perms.canEdit ? undefined : NO_EDIT}
                onClick={() => handlers.schedule(report)}
                className="self-start"
              >
                Schedule this report
              </Btn>
            </Panel>
          )}
          {report.health.length ? (
            <Btn size="sm" variant="quiet" disabled={!perms.canEdit} title={perms.canEdit ? undefined : NO_EDIT} onClick={() => handlers.schedule(report)} className="self-start">
              Add another schedule
            </Btn>
          ) : null}
        </aside>
      </div>

      <section aria-label="Delivery history for this report" className="mx-6 mb-10 rounded-[10px] border border-[#1E1E1E] bg-[#0B0B0B] p-4">
        <h2 className="mb-3 text-[15px] font-semibold text-[#F5F3EE]">Delivery history for this report</h2>
        {report.schedules.length === 0 ? (
          <p className="text-[12.5px] text-[#8F8F8F]">
            This report has no schedule, so nothing has been delivered for it. Files made by hand are listed under Runs.
          </p>
        ) : (
          <DeliveryTable
            rows={rows}
            history={data.delivery}
            loading={data.deliveryLoading}
            error={data.deliveryError}
            onRetry={data.retryDeliveries}
            filter={deliveryFilter}
            onFilter={onDeliveryFilter}
            now={data.now}
            actions={actions}
            perms={perms}
            label={`Deliveries of ${report.name}`}
            emptyTitle="Nothing has been delivered for this report yet"
            emptyBody="When its schedule runs, each run and each email it sends is recorded here."
          />
        )}
      </section>
    </div>
  )
}
